import React from 'react';
import { Link } from 'react-router-dom';

const PricingSection: React.FC = () => {
  const programs = [
    {
      name: 'Premium Program',
      price: '$4,995',
      description: 'Perfect for experienced buyers wanting assistance with their search and a buyer\'s advantage',
      features: [
        'One-on-one consultation with Certified Business Broker',
        'Personalized assessment of business goals and budget',
        'Feasibility & financial assessment',
        'Expert analysis of key financial Seller\'s Discretionary Earnings (SDE) metrics',
        'Customized search with 5 qualified businesses',
        'Access to on-market and off-market opportunities',
        'Comprehensive business summaries',
        'SBA loan guidance and approval support',
        'Step-by-step assistance with SBA forms',
        'Access to Certified SBA Loan Underwriter',
        'SBA pre-approval letter assistance',
        'Proof of funds documentation support'
      ],
      highlight: false,
      icon: 'ri-vip-crown-line',
      color: 'bg-teal-500'
    },
    {
      name: 'Elite Program',
      price: '$9,995',
      description: 'Comprehensive support for first time buyers seeking education and assistance throughout the process',
      features: [
        'Everything in Premium Program, plus:',
        'Customized search with 10 qualified businesses',
        'Extended 10-month advisory period',
        'Ongoing advising for businesses you discover',
        'Expert evaluation of opportunities you bring to us',
        'Continuous loan pre-approval assistance',
        'Deal structuring guidance throughout search',
        'Priority access to off-market opportunities',
        'Unlimited consultation calls during advisory period',
        'Negotiation strategy support',
        'Due diligence guidance for multiple opportunities',
        'Long-term partnership for acquisition success'
      ],
      highlight: true,
      icon: 'ri-vip-diamond-line',
      color: 'bg-amber-500'
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Choose Your Business Buyers Advantage Program
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Select the program that best fits your business acquisition goals. Both programs provide comprehensive support from certified professionals.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {programs.map((program, index) => (
            <div 
              key={index} 
              className={`rounded-3xl overflow-hidden transition-all duration-300 ${
                program.highlight 
                  ? 'bg-gradient-to-br from-amber-50 to-amber-100 border-4 border-amber-400 shadow-2xl transform lg:scale-105' 
                  : 'bg-white border-2 border-gray-200 shadow-lg hover:shadow-xl'
              }`}
            >
              {program.highlight && (
                <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-center py-3 font-bold text-sm uppercase tracking-wider">
                  <i className="ri-star-fill mr-2"></i>
                  Most Popular Choice
                </div>
              )}
              
              <div className="p-8">
                <div className="flex items-center justify-center mb-6">
                  <div className={`w-16 h-16 ${program.color} rounded-full flex items-center justify-center`}>
                    <i className={`${program.icon} text-white text-3xl`}></i>
                  </div>
                </div>

                <h3 className="text-3xl font-bold text-gray-900 text-center mb-2">
                  {program.name}
                </h3>
                
                <div className="text-center mb-6">
                  <div className="text-5xl font-bold text-gray-900 mb-2">
                    {program.price}
                  </div>
                  <p className="text-gray-600">{program.description}</p>
                </div>

                <div className="space-y-4 mb-8">
                  {program.features.map((feature, featureIndex) => (
                    <div 
                      key={featureIndex} 
                      className={`flex items-start ${
                        feature.startsWith('Everything') ? 'font-semibold text-gray-900 mt-4 pt-4 border-t-2 border-gray-200' : ''
                      }`}
                    >
                      <i className={`ri-check-line ${
                        program.highlight ? 'text-amber-600' : 'text-teal-500'
                      } text-xl mr-3 mt-0.5 flex-shrink-0`}></i>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <Link 
                  to="/consultation"
                  className={`block w-full text-center py-4 rounded-full font-semibold text-lg transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    program.highlight
                      ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 shadow-lg hover:shadow-xl transform hover:scale-105'
                      : 'bg-teal-500 text-white hover:bg-teal-600 shadow-md hover:shadow-lg'
                  }`}
                >
                  <i className="ri-calendar-check-line mr-2"></i>
                  Get Started with {program.name}
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <div className="bg-gray-50 p-8 rounded-2xl max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Not Sure Which Program is Right for You?
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              Schedule a free consultation with our certified business brokers to discuss your goals and find the perfect program for your needs.
            </p>
            <Link 
              to="/consultation"
              className="inline-block bg-white text-teal-600 border-2 border-teal-500 hover:bg-teal-500 hover:text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-question-line mr-2"></i>
              Schedule Free Consultation
            </Link>
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <div className="text-center p-6 bg-teal-50 rounded-2xl">
            <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-check-line text-white text-xl"></i>
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Certified Professionals</h4>
            <p className="text-gray-600 text-sm">Work with certified business brokers providing you with insider knowledge</p>
          </div>
          
          <div className="text-center p-6 bg-teal-50 rounded-2xl">
            <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-trophy-line text-white text-xl"></i>
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Business Financing Support</h4>
            <p className="text-gray-600 text-sm pb-3">Exclusive Access to Business Pre-Approval & SBA financing support</p>
          </div>
          
          <div className="text-center p-6 bg-teal-50 rounded-2xl">
            <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-customer-service-line text-white text-xl"></i>
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Business Buyers Advantage</h4>
            <p className="text-gray-600 text-sm">Gain the business buyers advantage through comprehensive guidance and access to off-market businesses</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;